// ─────────────────────────────────────────────────────────────────────────────
// FaqPage, the public question page at /faq.
//
// One data structure drives everything: SECTIONS holds six titled groups of
// questions, and the page renders them as kit <Accordion> blocks, builds the
// left rail from the same array, and emits the FAQPage JSON-LD from it too, so
// the marked-up answers can never drift from the visible ones.
//
// Layout is the reference site's two-column reading page: a sticky rail on the
// left listing the sections, the questions themselves on the right. Above them
// sits a short-answer panel that gets the awkward questions out of the way in
// one line each, because a buyer who reads "no live bank feed" on the first
// screen is a better conversation than one who finds out in month two.
//
// No photography exists yet, so the one image on the page is a <MediaSlot>.
// Nothing here names a customer, and nothing claims a capability the product
// does not ship.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CardTitle,
    BTN_SECONDARY,
    NUMS,
    Icon,
    Container,
    Section,
    Lead,
    Body,
    Eyebrow,
    MediaSlot,
    SectionHead,
    FeatureRow,
    SpecList,
    Accordion,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The questions, grouped ───────────────────────────────────────────────────
// Answers are plain strings so the same text can be handed to the accordion and
// to the FAQPage structured data without a second, drifting copy.
const SECTIONS = [
    {
        id: 'getting-started',
        label: 'Getting started',
        title: 'Getting started.',
        items: [
            {
                q: 'What is Wavelength?',
                a: 'It is the back office for operators who run more than one location. It closes each trading day where the day happened, proves the cash, and matches what the registers rang against what the card processor settled and what the bank actually received. Inventory, purchasing, receiving, production and wholesale invoicing run in the same workspace, off the same set of numbers.',
            },
            {
                q: 'Does it replace my point of sale?',
                a: 'No, and it is not trying to. Keep your register. Staff ring sales exactly the way they do today. Wavelength picks the day up after the last sale and carries it through the count, the deposit, the bank and the export your bookkeeper imports. There is a register app if you want one from us, but nothing in the platform assumes you use it.',
            },
            {
                q: 'Who is it built for?',
                a: 'Multi-location retail, grocery and specialty food, butcher and production counters, prepared food, and anyone selling wholesale alongside a shop front. The common thread is several locations, several tenders and an online store that all have to agree at the end of the day. One busy location with a production room fits as well. A single till with no back room probably does not need this.',
            },
            {
                q: 'How long does it take to get running?',
                a: 'A week is normal. To start we need a list of locations and the register each one runs, the people who need access and what each of them should see, and one recent bank statement as a CSV file. Then we connect a store, close one day together on a call, and import a month of bank activity so you can read the first exception list. Trading never stops, and the other locations come on when you are ready rather than all at once.',
            },
        ],
    },
    {
        id: 'your-register',
        label: 'How it works with your register',
        title: 'How it works with your register.',
        items: [
            {
                q: 'Which registers does Wavelength work with?',
                a: 'It is built to sit on top of whatever each store already runs. Where a system can hand its sales over, we read them directly. Where it cannot, sales arrive as a scheduled file instead. New sources are added on request, and we tell you what your particular setup involves before you commit to anything.',
            },
            {
                q: 'Do you have a ready-made connector for Toast, Clover or Square?',
                a: 'Not today. Wavelength works alongside those systems rather than plugging into them, and getting their sales in is a setup step we do with you. We would rather say that plainly than list logos we cannot stand behind. If your registers are on one of them, ask and we will tell you exactly what the first week looks like.',
            },
            {
                q: 'What about my online store?',
                a: 'WooCommerce is read directly, in a read only sync: orders, products and customers land next to counter sales in the same tables, with the same statuses and the same tenders. Other platforms come in as a file today. Either way, online and in store end up as one set of numbers rather than two that disagree.',
            },
            {
                q: 'Do you process payments or hold our money?',
                a: 'No. Wavelength is not a payment processor and not a gateway. No funds pass through it, and we never take a share of what you sell. You keep your processor and your rates. We read the settlement data to check that the money arrived where it was supposed to.',
            },
            {
                q: 'Which processor data can you read?',
                a: 'Datacap is the integration that is live today: card transactions and settlement batches come across on their own and are matched to orders and to deposits. Other processors are handled from their own statements and exports, which reconciles the same way with a file in place of a feed.',
            },
        ],
    },
    {
        id: 'money',
        label: 'Money and reconciliation',
        title: 'Money and reconciliation.',
        items: [
            {
                q: 'What exactly does Wavelength reconcile?',
                a: 'Three records of the same day that ought to agree and often do not: what the registers and the online store recorded as sales, what the processor says it settled and paid out, and what the bank statement shows arriving. Every day and every location is matched across all three. Anything that does not tie lands in one queue with the location, the source, the date and the amount.',
            },
            {
                q: 'Is there a live bank feed?',
                a: 'No. Bank data comes in as a statement file you export from your bank and upload, and duplicate rows are rejected on import so uploading the same month twice does not double anything. It is a deliberate choice as much as a limit: the statement is the record your bank stands behind, and it is the document your accountant will ask for.',
            },
            {
                q: 'What happens at the end of a trading day?',
                a: 'The manager counts the drawer blind, counts the safe, records the deposit and signs off. A short drawer cannot be closed without a written reason, a large variance needs an owner to approve it, and the day locks once it is signed. After that the figure is fixed, and any change is an entry on the audit log rather than a quiet edit.',
            },
            {
                q: 'Why are the counts blind?',
                a: 'A drawer that shows the expected total is not a count, it is a confirmation. Hiding the figure until the money is counted is the difference between a variance you can trust and one that always comes out at zero. Each count is recorded against the person, the drawer, the shift and the store, so over and short becomes a pattern you can read instead of a number you argue about.',
            },
            {
                q: 'What happens to something that does not match?',
                a: 'It goes to the exceptions queue and stays open until a person matches it or explains it. Every row carries where it came from, which location and day it belongs to and how much money is involved. Unconfirmed amounts are aged, so nothing sits quietly for a month waiting to be noticed at year end.',
            },
            {
                q: 'Can somebody change a day after it is closed?',
                a: 'Not silently. The day locks on sign-off, and every manual change anywhere in the system is written to a permanent audit log with the person, the time and what it was before. Stock movements are append-only in the same spirit: a correction is another entry, never an overwrite of the old one.',
            },
            {
                q: 'Does it track margin, food cost or profitability?',
                a: 'No. Wavelength tracks money in and whether it arrived, not what it cost you to earn. There is no cost of goods, no margin and no profit and loss inside the product, and we would rather you knew that now than found it in month two. What it hands your accountant is a set of daily figures that have already been proved against the bank.',
            },
            {
                q: 'Is Wavelength our accounting system?',
                a: 'No. It is the layer between the floor and the books. The closed day exports as a QuickBooks journal entry file that your bookkeeper imports, which is an export rather than a live connection, and your ledger stays where it already is. What changes is that the figures arriving in your accounts have already been matched to the bank.',
            },
        ],
    },
    {
        id: 'operations',
        label: 'Inventory, production and wholesale',
        title: 'Inventory, production and wholesale.',
        items: [
            {
                q: 'How does stock tracking work?',
                a: 'On hand is never a number somebody typed. It is derived from an append-only ledger of everything received, produced, sold, wasted, moved and counted, so you can read back exactly how a figure got to where it is. Adjustments are entries of their own, which is the reason the history stays worth reading.',
            },
            {
                q: 'Does it handle purchasing and receiving?',
                a: 'Yes. Reorder points and PAR levels feed a reorder board that turns low stock into a purchase order with the vendor and the quantities already filled in. The delivery is signed off at the back door on an iPad, with a temperature check against a 41°F limit, line by line variances against the order, and a report the office can read the same afternoon.',
            },
            {
                q: 'What happens with weighed items?',
                a: 'Weighed lines carry their weight and their price per pound the whole way through: the receipt, the sales report, the line item table and the export all read the same. A pack that sold at 1.96 pounds shows as 1.96 pounds at the price per pound, not as one unit at a mystery price.',
            },
            {
                q: 'Do you cover production and labels?',
                a: 'Every scale label printed becomes a production record, rolled up by product, by scale and by staff member, so what happened on the floor is already in the numbers before anyone opens a laptop. Label formats are set in the office and pushed out to the scales. Raw materials, yield and waste are tracked from input through to finished pack.',
            },
            {
                q: 'Can it invoice wholesale accounts?',
                a: 'Yes. A wholesale order raises its invoice automatically, priced off ten levels that each sit at a percentage off retail, with per customer overrides where an account has its own arrangement. Partial payments are recorded against the balance and receivables age on their own. Invoices are never deleted: a credit memo goes on the ledger instead, so the history stays intact. The PDF carries your branding.',
            },
        ],
    },
    {
        id: 'team',
        label: 'Your team and access',
        title: 'Your team and access.',
        items: [
            {
                q: 'Can a manager see only their own store?',
                a: 'Yes, and that is the usual setup. Access is scoped by role and by location, so a store manager sees their sales, their counts and their variances and nothing from the other locations, while the office sees the roll-up. Everyone reads the same records, each at the level their role allows.',
            },
            {
                q: 'How detailed are the permissions?',
                a: 'There are twenty modules and eight role presets, so most people are set up in one click and adjusted from there. Permissions cover both what someone can see and what they are allowed to approve, which is what keeps the short drawer approval and the locked day meaningful.',
            },
            {
                q: 'How do staff sign in on the floor?',
                a: 'Staff have a PIN and per app access, so the same person can be allowed on the scale and kept out of the office reports. Single use temporary codes cover the day you need to let somebody in once: the code dies the first time it is used rather than living on in a drawer.',
            },
            {
                q: 'Can we bring in a bookkeeper or a business partner?',
                a: 'Yes. Collaborators are invited by email and scoped like anyone else, so a bookkeeper can be given the close and the reconciliation and nothing more. There is a view-as mode, so you can see exactly what a role sees before you hand out the login.',
            },
        ],
    },
    {
        id: 'data',
        label: 'Data, pricing and support',
        title: 'Data, pricing and support.',
        items: [
            {
                q: 'How does pricing and billing work?',
                a: 'Pricing is published on the pricing page and charged per location per month, never as a percentage of what you sell. There is no self serve checkout: a person sets the plan up with you once we know how many locations you are running. No setup fee, no charge per register, and the companion apps are included.',
            },
            {
                q: 'What happens to our data if we leave?',
                a: 'It comes with you. Every table in the product exports to CSV, Excel or a branded PDF from the page you are looking at, and the closed day exports as a QuickBooks journal entry file. There is no export fee and nothing is held back. We would rather earn the renewal than keep you by making your own records hard to get out.',
            },
            {
                q: 'Where does the data live, and who can see it?',
                a: 'In your own workspace on Amazon Web Services, encrypted in transit and at rest, with role based access and an audit trail on every change. We do not sell your data, and we do not store card numbers: card details stay with your processor, and Wavelength works with payouts, amounts and references. Our own access is limited to what running and supporting the service requires.',
            },
            {
                q: 'What does support actually look like?',
                a: 'A person, not a ticket queue you shout into. Onboarding is included and we run your first close with you rather than sending a manual. After that you can reach us directly, and if you need a source we do not read yet, ask: adding one is a normal piece of work, not a special project.',
            },
        ],
    },
];

const SECTION_IDS = SECTIONS.map((s) => s.id);
const TOTAL_QUESTIONS = SECTIONS.reduce((n, s) => n + s.items.length, 0);
const ALL_QUESTIONS = SECTIONS.flatMap((s) => s.items);

// The one-line versions of the questions people are most nervous about asking.
const SHORT_ANSWERS_LEFT = [
    { label: 'Replaces your point of sale', value: 'No' },
    { label: 'Live bank feed', value: 'No, statements import by CSV' },
    { label: 'Tracks margin or food cost', value: 'No' },
    { label: 'Takes a share of your sales', value: 'No' },
];

const SHORT_ANSWERS_RIGHT = [
    { label: 'Your accounting system', value: 'No, it exports to yours' },
    { label: 'Your data if you leave', value: 'Exports in full' },
    { label: 'Locations it runs', value: 'As many as you have' },
    { label: 'Time to a first closed day', value: 'About a week' },
];

// ── Rail: which section you are reading, and how to skip to another ──────────
// Native anchors do the scrolling; an observer only decides which row is lit.
function useActiveSection(ids) {
    const [active, setActive] = useState(ids[0]);
    useEffect(() => {
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length || typeof IntersectionObserver !== 'function') return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                const first = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (first) setActive(first.target.id);
            },
            { rootMargin: '-140px 0px -55% 0px', threshold: 0 },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [ids]);
    return active;
}

function QuestionRail({ active }) {
    return (
        <nav aria-label="Question sections" className="flex flex-col gap-4 lg:sticky lg:top-28">
            <Eyebrow>On this page</Eyebrow>
            <div className="overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white">
                {SECTIONS.map((s, i) => (
                    <a
                        key={s.id}
                        href={`#${s.id}`}
                        className={`flex items-baseline justify-between gap-4 px-5 py-4 text-[14px] leading-[1.4] transition-colors ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''} ${active === s.id ? 'bg-[#F5F5F5] font-medium text-[#0A0A0A]' : 'text-[#6B6B6B] hover:text-[#0A0A0A]'}`}
                    >
                        <span>{s.label}</span>
                        <span className={`${NUMS} flex-none text-[12px] text-[#A1A1A1]`}>
                            {String(s.items.length).padStart(2, '0')}
                        </span>
                    </a>
                ))}
            </div>
            <div className="rounded-[24px] bg-[#F5F5F5] p-6">
                <Body className="text-[13.5px]">
                    If your question is not here, ask it directly. A person answers, not a form.
                </Body>
                <Link to="/contact" className={`${BTN_SECONDARY} mt-4`}>
                    Talk to us
                    <Icon name="arrow" className="h-4 w-4" />
                </Link>
            </div>
        </nav>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function FaqPage() {
    const active = useActiveSection(SECTION_IDS);

    return (
        <MarketingShell>
            <Seo
                title="FAQ: what Wavelength does, and what it does not"
                description="Answers about Wavelength: whether it replaces your point of sale, how card, cash and bank reconcile, how bank statements import by CSV, and how pricing works."
                path="/faq"
                keywords="wavelength faq, retail back office questions, does it replace my pos, bank statement csv import, daily close and blind cash count, multi location retail reconciliation, exceptions queue, quickbooks journal entry export, wholesale invoicing questions"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'FAQPage',
                            '@id': 'https://wavelengtherp.com/faq#faq',
                            name: 'Wavelength frequently asked questions',
                            url: 'https://wavelengtherp.com/faq',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                            mainEntity: ALL_QUESTIONS.map((f) => ({
                                '@type': 'Question',
                                name: f.q,
                                acceptedAnswer: { '@type': 'Answer', text: f.a },
                            })),
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://wavelengtherp.com/faq' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Frequent questions"
                title="Answers, including the awkward ones."
                lead={`${TOTAL_QUESTIONS} questions we are actually asked, answered the way we would answer them on a call. Where the answer is no, it says no.`}
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />

            {/* ── Short answers ──────────────────────────────────────────── */}
            <Section tone="paper2" pad="md">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="The short answers."
                            lead="The ones worth settling before you read anything else. If one of these is a deal breaker, better to find out on this page than in month two."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <SpecList rows={SHORT_ANSWERS_LEFT} />
                            <SpecList rows={SHORT_ANSWERS_RIGHT} />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── The questions ──────────────────────────────────────────── */}
            <Section id="questions" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[300px_1fr] lg:gap-16">
                        <Reveal>
                            <QuestionRail active={active} />
                        </Reveal>
                        <Reveal delay={80}>
                            <div className="flex flex-col gap-14">
                                {SECTIONS.map((s, i) => (
                                    <div key={s.id} id={s.id} className="scroll-mt-28">
                                        <CardTitle>{s.title}</CardTitle>
                                        <Accordion className="mt-6" items={s.items} defaultOpen={i === 0 ? 0 : -1} />
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── The offer that settles the rest ────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Still deciding"
                            title="Bring one bank statement."
                            body="Most of these questions settle faster by watching a day of your own trading go through the system than by reading another page about it. We close one day with you and import one statement, and you read whatever falls out."
                            bullets={[
                                'One location, one day, one statement to start with',
                                'We run the first close on the call rather than sending a manual',
                                'Whatever the day produces is yours, exported',
                                'Nothing changes about how your staff ring a sale',
                            ]}
                            cta={
                                <Link to="/contact" className={BTN_SECONDARY}>
                                    Book a walkthrough
                                    <Icon name="arrow" className="h-4 w-4" />
                                </Link>
                            }
                            media={
                                <MediaSlot
                                    kind="Screenshot"
                                    label="The exceptions list after a first statement import"
                                    note="Laptop screenshot, one worklist, each row carrying a location, a date and an amount"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── One more prompt to ask ─────────────────────────────────── */}
            <Section pad="md">
                <Container>
                    <Reveal>
                        <div className="flex flex-col gap-3 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white px-6 py-7 sm:px-8">
                            <CardTitle>Ask the one that is not on this page.</CardTitle>
                            <Lead className="max-w-[620px] text-[15px]">
                                We would rather answer a hard question early than win a customer who is surprised later. Tell us how your day closes now and we will tell you, plainly, which parts of it this replaces and which parts it does not.
                            </Lead>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Talk to us"
                title="Close one day and judge it on that."
                body="Start with a single location and a single statement. If nothing turns up in the first week, you have lost an evening."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'Email us', href: 'mailto:support@wavelengtherp.com' }}
            />
        </MarketingShell>
    );
}
